import Dexie, { type Table } from 'dexie';
import type {
  EventId,
  GameDefinition,
  GameDefinitionId,
  ImageId,
  ImageRecord,
  Player,
  PlayerEvent,
  PlayerGroup,
  PlayerGroupId,
  PlayerId,
  SessionEvent,
  SessionId,
  SessionMetadata,
  SessionSnapshot,
  SessionTemplate,
  SessionTemplateId,
  TeamId,
  TeamPreset,
  UserId,
} from '@/schemas';
import { PlayerEventSchema } from '@/schemas';

type SyncStateRecord = {
  id?: string;
  sessionId: string;
  userId?: string;
  entityType?:
    | 'player_events'
    | 'game_definitions'
    | 'team_presets'
    | 'player_groups'
    | 'session_index'
    | 'session_templates'
    | 'images';
  lastUploadedAt?: number;
  lastDownloadedAt?: number;
};

export const DATABASE_VERSION = 10;

// Common store definitions to reduce duplication
const BASE_STORES_V1 = {
  sessionEvents: 'id, sessionId, timestamp, type',
  playerEvents: 'id, timestamp, type, payload.playerId',
  players: 'id, name, createdAt, deletedAt',
  gameDefinitions: 'id, name, createdAt, deletedAt',
  teamPresets: 'id, name, createdAt, deletedAt',
  sessionMetadata: 'id, startedAt, endedAt, deletedAt, gameDefinitionId, status',
  syncState: 'sessionId, userId, lastUploadedAt, lastDownloadedAt',
} as const;

const BASE_STORES_V2 = {
  sessionEvents: 'id, sessionId, timestamp, type, userId',
  playerEvents:
    'id, timestamp, type, userId, payload.playerId, payload.sourcePlayerId, payload.targetPlayerId',
  players: 'id, name, userId, createdAt, deletedAt',
  gameDefinitions: 'id, name, userId, createdAt, deletedAt',
  teamPresets: 'id, name, userId, createdAt, deletedAt',
  sessionMetadata: 'id, startedAt, endedAt, deletedAt, gameDefinitionId, status, userId',
  syncState: 'sessionId, userId, entityType, lastUploadedAt, lastDownloadedAt',
} as const;

const BASE_STORES_V3 = {
  ...BASE_STORES_V2,
  sessionMetadata:
    'id, startedAt, endedAt, deletedAt, archivedAt, gameDefinitionId, status, userId',
} as const;

const BASE_STORES_V4 = {
  ...BASE_STORES_V2,
  playerGroups: 'id, name, userId, createdAt, deletedAt',
  sessionMetadata: 'id, startedAt, endedAt, deletedAt, gameDefinitionId, status, userId',
} as const;

const BASE_STORES_V5 = {
  ...BASE_STORES_V2,
  playerGroups: 'id, name, userId, createdAt, deletedAt',
  sessionMetadata:
    'id, startedAt, endedAt, deletedAt, archivedAt, gameDefinitionId, status, userId',
} as const;

const BASE_STORES_V6 = {
  ...BASE_STORES_V5,
  sessionSnapshots: 'id, sessionId, createdAt, [sessionId+createdAt]',
} as const;

const BASE_STORES_V7 = {
  ...BASE_STORES_V6,
  sessionTemplates: 'id, name, userId, createdAt, deletedAt',
} as const;

const BASE_STORES_V8 = {
  ...BASE_STORES_V7,
  players: 'id, name, userId, createdBy, createdAt, deletedAt',
} as const;

const BASE_STORES_V9 = {
  ...BASE_STORES_V8,
  images:
    'id, entityType, entityId, uploadStatus, uploadedAt, sessionId, createdAt, [entityType+entityId], [sessionId+uploadedAt]',
} as const;

const BASE_STORES_V10 = {
  ...BASE_STORES_V9,
  players: 'id, name, userId, handle, createdBy, createdAt, deletedAt',
  sessionMetadata:
    'id, startedAt, endedAt, deletedAt, archivedAt, gameDefinitionId, status, userId, inviteToken',
} as const;

export class OneScoreDatabase extends Dexie {
  // Session events (event sourcing - append only)
  sessionEvents!: Table<SessionEvent, EventId>;

  // Player events (event sourcing - append only)
  playerEvents!: Table<PlayerEvent, EventId>;

  // Players (computed from playerEvents, cached for queries)
  players!: Table<Player, PlayerId>;

  // Game definitions (CRUD)
  gameDefinitions!: Table<GameDefinition, GameDefinitionId>;

  // Team presets (CRUD)
  teamPresets!: Table<TeamPreset, TeamId>;

  // Player groups (CRUD)
  playerGroups!: Table<PlayerGroup, PlayerGroupId>;

  // Session metadata (denormalized cache for fast list views)
  sessionMetadata!: Table<SessionMetadata, SessionId>;

  // Sync cursors per session or per user entity
  syncState!: Table<SyncStateRecord, string>;

  // Session snapshots (for fast replay of large sessions)
  sessionSnapshots!: Table<SessionSnapshot, string>;

  // Session templates (CRUD)
  sessionTemplates!: Table<SessionTemplate, SessionTemplateId>;

  // Images (offline-first image storage)
  images!: Table<ImageRecord, ImageId>;

  constructor() {
    super('OneScoreDB');

    // Version 1: Initial schema
    this.version(1).stores(BASE_STORES_V1);

    // Version 2: Added userId fields and entityType to syncState
    this.version(2)
      .stores(BASE_STORES_V2)
      .upgrade(async (tx) => {
        const syncStateTable = tx.table<SyncStateRecord, string>('syncState');
        await syncStateTable.toCollection().modify((state) => {
          state.id = state.id ?? state.sessionId;
          if (!('entityType' in state)) {
            // Preserve existing shape; undefined keeps index optional.
            state.entityType = state.entityType ?? undefined;
          }
        });
      });

    // Version 3: Added archivedAt to sessionMetadata
    this.version(3).stores(BASE_STORES_V3);

    // Version 4: Added playerGroups table
    this.version(4).stores(BASE_STORES_V4);

    // Version 5: Added archivedAt to sessionMetadata (fixing v4)
    this.version(5)
      .stores(BASE_STORES_V5)
      .upgrade(async (tx) => {
        const metadataTable = tx.table<SessionMetadata, SessionId>('sessionMetadata');
        await metadataTable.toCollection().modify((record) => {
          if (!('archivedAt' in record)) {
            record.archivedAt = record.archivedAt ?? undefined;
          }
        });
      });

    // Version 6: Added sessionSnapshots table for fast replay
    this.version(6).stores(BASE_STORES_V6);

    // Version 7: Added sessionTemplates table
    this.version(7).stores(BASE_STORES_V7);

    // Version 8: Added createdBy field to players table
    this.version(8)
      .stores(BASE_STORES_V8)
      .upgrade(async (tx) => {
        // Migrate existing players by finding PlayerCreated events to populate createdBy
        const playersTable = tx.table<Player, PlayerId>('players');
        const playerEventsTable = tx.table<PlayerEvent, EventId>('playerEvents');

        const allPlayers = await playersTable.toArray();
        const allEvents = await playerEventsTable.orderBy('timestamp').toArray();

        // Find PlayerCreated events and map to playerId
        const createdByMap = new Map<PlayerId, UserId | undefined>();
        for (const event of allEvents) {
          const parsedEvent = PlayerEventSchema.parse(event);
          if (parsedEvent.type === 'PlayerCreated') {
            createdByMap.set(parsedEvent.payload.playerId, parsedEvent.userId ?? undefined);
          }
        }

        // Update each player with createdBy from PlayerCreated event
        for (const player of allPlayers) {
          // Check if we have a PlayerCreated event for this player
          if (createdByMap.has(player.id)) {
            const createdBy = createdByMap.get(player.id);
            await playersTable.update(player.id, {
              createdBy, // Can be undefined if created by guest
            });
          }
        }
      });

    // Version 9: Added images table for image management
    this.version(9).stores(BASE_STORES_V9);

    // Version 10: Added handle field to players, inviteToken to sessionMetadata, spectator support
    this.version(DATABASE_VERSION)
      .stores(BASE_STORES_V10)
      .upgrade(async () => {
        // No data migration needed - new fields are optional/nullable
        // Indexes added automatically by Dexie
      });
  }
}

export const db = new OneScoreDatabase();
